import { ref, watch } from 'vue'
import { defineStore, storeToRefs } from 'pinia'
import { useWebSocket } from '@vueuse/core';
import { useAuthenticationStore } from '@/stores/authentication.js'
import { useToastStore } from '@/stores/toast.js'
import { useI18n } from 'vue-i18n'

export const useSocketStore = defineStore('socket', () => {

    const authenticationStore = useAuthenticationStore()
    const toastStore = useToastStore()
    const { t } = useI18n()

    const { currentUser } = storeToRefs(authenticationStore);

    const websocketUrl = ref('');

    const getWebSocketUrlWithToken = (token) => {
        const protocol = import.meta.env.VITE_APP_ENV === "development" ? "ws" : "wss";
        if (!token) {
            throw new Error("Token is required");
        }
        return `${protocol}://${import.meta.env.VITE_BACKEND_URL.split('://')[1]}/cable?token=${token}`;
    };

    const { status, data, send, open, close } = useWebSocket(
        websocketUrl,
        {
            immediate: false,
            autoReconnect: true,
            heartbeat: {
            message: JSON.stringify({
                command: 'message',
                identifier: JSON.stringify({ channel: 'AutowiseChannel' }),
                data: JSON.stringify({ type: 'ping' })
            }),
            interval: 1000,
            pongTimeout: 10000,
            },
            onConnected: (ws) => {
                send(JSON.stringify({
                    command: 'subscribe',
                    identifier: JSON.stringify({ channel: 'AutowiseChannel'})
                }));
            },
            onDisconnected: (ws, event) => {
                console.info('WebSocket disconnected:', event);
            },
            onError: (ws, event) => {
                console.info('WebSocket error:', event);
            },
            onMessage: (ws, event) => {
                /**
                 * WebSocket Message Formats:
                 * 
                 * 1. Welcome: {type: 'welcome'}
                 * 2. Subscription Confirmation: {identifier: '{"channel":"AutowiseChannel"}', type: 'confirm_subscription'}
                 * 3. Ping: {type: 'ping', message: 1764269025}
                 * 4. Channel Messages: {identifier: '{"channel":"AutowiseChannel"}', message: {...}}
                 *    - Pong: {type: 'pong', timestamp: 1764269026}
                 *    - Payment: {type: 'payment', status: 'success', data: {quotas: {...}}}
                 *    - Quote Status: {type: 'quote_status', data: {status: 'completed|delayed|expiring', quote_id: 123, vehicle_name: '...'}}
                 */
                const parsedData = JSON.parse(event.data);
                
                if (parsedData.type === 'ping' || parsedData.type === 'welcome' || parsedData.type === 'confirm_subscription') {
                    return;
                }
                
                const message = parsedData.message;
                if (!message) return;
                
                if (message.type == 'payment' && message.status == "success") {
                    const quotas = message.data.quotas;
                    currentUser.value.user.attributes.additional_quota = quotas.additional_quota;
                    currentUser.value.user.attributes.monthly_quota = quotas.monthly_quota;
                    currentUser.value.user.attributes.yearly_quota = quotas.yearly_quota;
                }
                
                if (message.type === 'quote_status') {
                    handleQuoteStatusNotification(message);
                }
            }
        }
    );

    /**
     * Checks if a specific notification type is enabled in user preferences
     * @param {string} notificationType - The notification type to check (e.g., 'quote_returned', 'quote_delayed', 'quote_expiring')
     * @returns {boolean} True if notifications are enabled globally and the specific type is enabled
     */
    const isNotificationEnabled = (notificationType) => {
        if (!toastStore.toastsEnabled) return false;
        
        const enabled = localStorage.getItem(`websiteNotifications_${notificationType}`);
        return enabled ? JSON.parse(enabled) : true;
    };

    /**
     * Handles incoming quote status notifications from WebSocket and displays appropriate toast messages
     * @param {Object} message - The WebSocket message object
     * @param {Object} message.data - The message data
     * @param {string} message.data.status - Quote status: 'completed', 'delayed', or 'expiring'
     * @param {number} message.data.quote_id - The ID of the quote
     * @param {string} message.data.vehicle_name - The name of the vehicle associated with the quote
     */
    const handleQuoteStatusNotification = (message) => {
        const { status, quote_id, vehicle_name } = message.data || {};
        
        if (status === 'completed' && isNotificationEnabled('quote_returned')) {
            toastStore.success(
                t('toast.quote_completed', { vehicle: vehicle_name || 'your vehicle' })
            );
        } else if (status === 'delayed' && isNotificationEnabled('quote_delayed')) {
            toastStore.warning(
                t('toast.quote_delayed', { vehicle: vehicle_name || 'your vehicle' })
            );
        } else if (status === 'expiring' && isNotificationEnabled('quote_expiring')) {
            toastStore.warning(
                t('toast.quote_expiring', { vehicle: vehicle_name || 'your vehicle' })
            );
        }
    };

    watch(currentUser, async (newUser, oldUser) => {
        if (newUser && newUser.token) {
            console.info('User signed in. Attempting WebSocket connection...');
            websocketUrl.value = getWebSocketUrlWithToken(newUser.token);
            open();
        } else {
            console.info('User signed out or no token. Closing WebSocket connection...');
            close();
        }
    }, { deep: true, immediate: true });

    function $reset() {}

    return {
        status,
        $reset
    }

}, {
  persist: {
    // storage: sessionStorage,
  }
})